// The mounted Work Mode owns execution. A translated request is not a result.
let owner = null;
const commands = new Map();
export const LESSON_COMMANDS = new Set([
  'set_destination', 'repeat', 'adapt', 'level', 'manoeuvres',
  'minor', 'major', 'observation', 'end_lesson', 'confirm_destination', 'cancel_destination',
]);
const unavailable = () => ({ ok: false, message: 'That lesson control is no longer available. Open the active lesson and try again.' });

export function registerLessonCommandHandler(lessonId, handler) {
  const registration = { lessonId: String(lessonId), handler };
  owner = registration;
  return () => { if (owner === registration) owner = null; };
}

// Capture when the utterance is sent, before any server/network wait. An old
// response must not execute in a newly mounted instance of the same lesson.
export function captureLessonCommandScope() {
  return owner;
}

export function lessonDestinationPending(lessonId) {
  return Boolean(owner && owner.lessonId === lessonId && owner.pendingDestinationUntil > Date.now());
}

export async function executeLessonCommand(command, requestScope = owner) {
  const current = owner;
  if (!command || !current || requestScope !== current || command.lesson_id !== current.lessonId
      || !LESSON_COMMANDS.has(command.action) || typeof current.handler !== 'function'
      || !command.command_id) return unavailable();
  const issuedAt = Date.parse(command.issued_at);
  const age = Date.now() - issuedAt;
  if (!Number.isFinite(issuedAt) || age < -5000 || age > 60000) return unavailable();
  const key = `${command.lesson_id}:${command.command_id}`;
  const signature = JSON.stringify(command);
  const existing = commands.get(key);
  if (existing) return existing.owner === current && existing.signature === signature ? existing.promise : unavailable();
  // Promise assignment precedes execution, so repeated tool deliveries share it.
  const promise = Promise.resolve().then(async () => {
    if (owner !== current) return unavailable();
    try {
      const result = await current.handler(command);
      if (owner !== current && !(command.action === 'end_lesson' && owner === null)) return unavailable();
      if (owner === current) current.pendingDestinationUntil = result?.pending_confirmation ? Date.now() + 90000 : 0;
      if (result && typeof result.ok === 'boolean' && typeof result.message === 'string') return result;
      return { ok: false, message: 'The lesson control did not confirm a result. Check the lesson screen before trying again.' };
    } catch (error) {
      return { ok: false, message: 'The lesson control could not complete. Check the lesson screen and try again.' };
    }
  });
  commands.set(key, { signature, promise, owner: current });
  // Bounded per-tab duplicate protection, without retaining pupil or GPS data.
  if (commands.size > 100) commands.delete(commands.keys().next().value);
  return promise;
}

export async function applyLessonCommandEnvelope(envelope, requestScope = owner) {
  if (envelope?.specialist_id !== 'ui_lesson_control' || envelope?.dispatch_result?.outcome !== 'lesson_command') return null;
  const result = await executeLessonCommand(envelope.dispatch_result.lesson_command, requestScope);
  return { ...envelope, narration: result.message, dispatch_result: {
    ...envelope.dispatch_result, outcome: result.ok ? 'lesson_control_completed' : 'unavailable',
    lesson_command: undefined, narration_hint: result.message, control_result: result,
  } };
}

// Work Mode supplies the exact callbacks and disabled flags used by its buttons.
export async function runLessonControl(command, { actions, disabled = {}, adaptRoute, destination }) {
  const action = command.action;
  const key = action === 'end_lesson' ? 'end-lesson'
    : action === 'confirm_destination' ? 'set_destination' : action;
  if (disabled[key]) return { ok: false, message: 'That lesson control is unavailable while the route is updating or returning to the pupil.' };
  if (['set_destination', 'confirm_destination', 'cancel_destination'].includes(action)) {
    if (typeof destination !== 'function') return { ok: false, message: 'The lesson map is not ready to set a destination yet.' };
    return destination(command);
  }
  if ((action === 'level' && Number.isInteger(command.teaching_level))
      || (action === 'adapt' && typeof command.teaching_goal === 'string' && command.teaching_goal.trim())) {
    if (typeof adaptRoute !== 'function') return unavailable();
    const accepted = await adaptRoute(command.teaching_level, command.teaching_goal);
    return { ok: accepted === true, message: accepted === true
      ? 'The lesson route has been updated, keeping the agreed return time.'
      : 'The route change did not complete. Check the lesson screen for the reason.' };
  }
  if (typeof actions?.[key] !== 'function') return unavailable();
  const result = await actions[key]();
  if (['repeat', 'minor', 'major'].includes(action)) {
    return { ok: result === true, message: result === true
      ? (action === 'repeat' ? 'The repeat route is ready.' : `The ${action} marker has been saved.`)
      : 'That action did not confirm completion. Check the lesson screen before trying again.' };
  }
  return { ok: true, message: {
    adapt: 'Adapt is open. What would you like to work on?',
    level: 'The level choices are open. Which level would you like?',
    manoeuvres: 'The manoeuvres control is open. Complete the parked-learning check before starting.',
    observation: 'The observation control is open.',
    end_lesson: 'The lesson close-out is open.',
  }[action] || 'The lesson control is open.' };
}
